import { GetQueryVariables } from "api/apiFunctions";
import { getRequest } from "api/commonActions/get";
import { postRequest } from "api/commonActions/post";
import { patchRequest } from "api/commonActions/update";
import { AxiosResponse } from "axios";
import { Requests } from "config/api";
import { QueryParamsProps } from "declarations/commonServices";
import {
  GetAgentsProps,
  GetAgentsReturnType,
  GetOneStaffProps,
  GetOneStaffReturnType,
  GetStaffProps,
  GetStaffReturnType,
  GetUserProps,
  GetUserReturnType,
  GetUsersProps,
  GetUsersReturnType,
  LoginProps,
  LoginReturnType,
  RegisterStaffProps,
  RegisterStaffReturnType,
  SuspendUserProps,
  SuspendUserReturnType,
  UnsuspendUserProps,
  UnsuspendUserReturnType,
  UpdateUserProps,
  UpdateUserReturnType,
  UpdateUserTypeProps,
  UpdateUserTypeReturnType,
  UserSuspensionProps,
  UserSuspensionReturnType,
} from "declarations/userServices";

export const Login = async ({
  email,
  password,
  setLoading,
}: LoginProps): Promise<LoginReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await postRequest({
      route: Requests.post.login,
      payload: { email, password },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const user = response.data?.user;
    const token = response.data?.token;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      user,
      token,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const RegisterStaff = async ({
  email,
  password,
  setLoading,
  name,
  phoneNumber,
}: RegisterStaffProps): Promise<RegisterStaffReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await postRequest({
      route: Requests.post.registerStaff,
      payload: { email, password, name, phoneNumber, role: "Staff" },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const staff = response.data?.user;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      staff,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const GetAgents = async ({
  limit,
  offset,
  setLoading,
}: GetAgentsProps): Promise<GetAgentsReturnType> => {
  let queryParams: QueryParamsProps = GetQueryVariables({
    limit,
    offset,
  });

  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getAgents,
      queryParams,
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const agents = response.data?.agents;
    const count = response.data?.count;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      agents,
      count,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const GetStaff = async ({
  limit,
  offset,
  setLoading,
}: GetStaffProps): Promise<GetStaffReturnType> => {
  let queryParams: QueryParamsProps = GetQueryVariables({
    limit,
    offset,
  });

  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getStaff,
      queryParams,
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const staff = response.data?.users;
    const count = response.data?.count;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      staff,
      count,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const GetOneStaff = async ({
  setLoading,
  id,
}: GetOneStaffProps): Promise<GetOneStaffReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getUser,
      pathVariables: {
        id,
      },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const staff = response.data?.user;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      staff,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const GetUsers = async ({
  limit,
  offset,
  setLoading,
  search,
}: GetUsersProps): Promise<GetUsersReturnType> => {
  let queryParams: QueryParamsProps = GetQueryVariables({
    limit,
    offset,
    search,
  });

  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getUsers,
      queryParams,
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const users = response.data?.users;
    const count = response.data?.count;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      users,
      count,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const GetUser = async ({
  setLoading,
  id,
}: GetUserProps): Promise<GetUserReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getUser,
      pathVariables: {
        id,
      },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const user = response.data?.user;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      user,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const UpdateUser = async ({
  setLoading,
  id,
  payload,
}: UpdateUserProps): Promise<UpdateUserReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await patchRequest({
      route: Requests.update.updateUser,
      pathVariables: {
        id,
      },
      payload,
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const user = response.data?.user;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      user,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const UpdateUserType = async ({
  setLoading,
  id,
  type,
}: UpdateUserTypeProps): Promise<UpdateUserTypeReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await patchRequest({
      route: Requests.update.updateUserType,
      pathVariables: {
        id,
      },
      payload: {
        type,
      },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const user = response.data?.user;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      user,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const UserSuspension = async ({
  setLoading,
  id,
  isActive,
}: UserSuspensionProps): Promise<UserSuspensionReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await patchRequest({
      route: Requests.update.updateUser,
      pathVariables: {
        id,
      },
      payload: {
        isActive,
      },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const user = response.data?.user;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      user,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const SuspendUser = async ({
  setLoading,
  user,
  date,
}: SuspendUserProps): Promise<SuspendUserReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await postRequest({
      route: Requests.post.suspendUser,
      payload: {
        user,
        date,
      },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const UnsuspendUser = async ({
  setLoading,
  user,
}: UnsuspendUserProps): Promise<UnsuspendUserReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await postRequest({
      route: Requests.post.unSuspendUser,
      payload: {
        user,
      },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};
